import type { ExitReviewResponse } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatCurrency } from "../../lib/money";

interface ReviewAdvancesSectionProperties {
  data?: ExitReviewResponse;
  isLoading: boolean;
  isError: boolean;
}

// "Review Advances" block of the exit modal, in parity with Step 6's exit screen.
// A figure whose BFF leg failed is absent from the response, so it renders as a
// dash rather than a misleading $0.00 / 0.
function ReviewAdvancesSection({ data, isLoading, isError }: ReviewAdvancesSectionProperties) {
  const { t } = useTranslation();
  const notAvailable = t("exit.notAvailable");
  const stipulations = data?.openStipulationCount?.openStipulationCount;
  const advanceAdjustment = data?.internalAdvance?.totalAdvanceAdjustmentExcludingManual;
  const amountPaidToDealer = data?.dealStatus?.amountPaidToDealer;
  const hasWarnings = !!data?.metadata?.warnings?.length;

  const rows = [
    { key: "stipulations", label: t("exit.reviewAdvances.stipulations"), value: stipulations },
    {
      key: "advanceAdjustment",
      label: t("exit.reviewAdvances.advanceAdjustment"),
      value: advanceAdjustment === undefined ? undefined : formatCurrency(advanceAdjustment),
    },
    {
      key: "amountPaidToDealer",
      label: t("exit.reviewAdvances.amountPaidToDealer"),
      value: amountPaidToDealer === undefined ? undefined : formatCurrency(amountPaidToDealer),
    },
  ];

  return (
    <section>
      <h4 className="text-primary mb-1 text-xs font-semibold">{t("exit.reviewAdvances.heading")}</h4>
      {isError ? (
        <p role="alert" className="text-destructive text-xs">
          {t("exit.reviewAdvances.loadError")}
        </p>
      ) : (
        <div className="border-border bg-muted flex flex-col gap-1 rounded-md border p-2">
          {isLoading ? (
            <p className="text-muted-foreground text-xs">{t("exit.reviewAdvances.loading")}</p>
          ) : (
            <>
              {rows.map(({ key, label, value }) => (
                <div key={key} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="text-primary" data-testid={`review-advances-${key}`}>
                    {value ?? notAvailable}
                  </span>
                </div>
              ))}
              {hasWarnings && (
                <p role="status" className="text-warning text-2xs">
                  {t("exit.reviewAdvances.partialWarning")}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default ReviewAdvancesSection;
